<template>
  <div class="layout-row flex-gt-sm-100 flex-100 layout-wrap">

    <!-- OP Datum -->

    <div class="flex-gt-sm-33 flex-100 px-2">
      <validation-provider tag="div"
                           :name="path + '.op_datum'"
                           :ref="path + '.op_datum'"
                           :rules="{required:true,german_date:true,date_not_in_future:true,date_must_be_greater_than_diagnosis:diagDate, date_must_be_lesser_than_death: [deathDate, null]}"
                           v-slot="{ errors }">
        <lx-datepicker
            :color="compColor.textInput"
            :auto-focus="!formId"
            :name="path + '.op_datum'"
            :label="$t('FORMS_DIVERSE.BASE_OPERATION_DATE') + ' *'"
            class="mb-6"
            :disabled="isDisabled"
            :dense="denseFields"
            :hide-details="!errors || errors.length <= 0"
            :error="errors[0]"
            v-model="model.op_datum"
        ></lx-datepicker>
      </validation-provider>
    </div>

    <div class="flex-100 px-2">

      <!-- OP Intension -->

      <validation-provider tag="div"
                           :name="path + '.op_intention'"
                           :ref="path + '.op_intention'"
                           rules="required"
                           v-slot="{ errors }">
        <fake-input-frame :label="$t('FORMS_DIVERSE.BASE_OPERATION_OP_INTENSION') + ' *'"
                          :disabled="isDisabled"
                          :errors="errors.length > 0">
          <div class="layout-column px-4 py-2 flex">
            <v-radio-group :dense="denseFields" v-model="model.op_intention" :error-messages="errors[0]" hide-details class="ma-0 pa-0" style="flex:unset;margin-top: 10px;">
              <v-radio v-for="(value, key) in OP_IntentionTypes"
                       :label="value.text"
                       :title="value.title"
                       :value="key"
                       :key="key"
                       :disabled="isDisabled"
                       :color="compColor.isInDarkMode ? compColor.primeHighlightColor : compColor.primeColor"
                       class="mb-1"
                       :dark="compColor.isInDarkMode">
              </v-radio>
            </v-radio-group>
          </div>
        </fake-input-frame>
        <span class="error--text" style="line-height:12px;font-size:12px;margin-top: 3px;margin-left:10px;">{{ errors[0] }}</span>
      </validation-provider>
    </div>

    <div class="flex-100 px-2">
      <fake-input-frame :label="$t('FORMS_DIVERSE.BASE_OPERATION_OPS') + ' *'"
                        :disabled="isDisabled">
        <div class="layout layout-column px-2 pb-1">
          <div class="flex-gt-sm-100 flex-100 px-2">
            <!-- hidden input field to show error, if array of ops is empty -->
            <validation-provider tag="div"
                                 :name="path + '.op_ops'"
                                 :ref="path + '.op_ops'"
                                 rules="required|at_least_one_op_is_selected"
                                 v-slot="{ errors }">
              <lx-alert-box v-show="errors[0] && (!model.op_ops || model.op_ops.length<1)" color="red" class="my-2">
                {{ $t('ERROR_EMPTY_OPS_ARRAY') }}
              </lx-alert-box>

              <v-text-field
                  v-model="model.op_ops"
                  v-show="false"
                  :dense="denseFields"
                  outlined
                  :error-messages="errors[0]"
              ></v-text-field>
            </validation-provider>
          </div>

          <div v-bind:style="{'z-index': '4'}" class="layout-align-start-center pa-1 my-1 ml-1">
              <lx-btn :disabled="isDisabled"
                      icon="add_circle"
                      :color="compColor.primeColor"
                      :iconColor="'white'"
                      :label="$t('FORMS_DIVERSE.BASE_OPERATION_ADD_NEW_OP')"
                      :tooltip="$t('FORMS_DIVERSE.BASE_OPERATION_ADD_NEW_OP')"
                      iconSize="21"
                      :width="'auto'"
                      :height="35"
                      @click="addOPSItem(path + '.op_ops', {})"
              ></lx-btn>
          </div>

          <div :key="index" v-for="(item, index) in model.op_ops"
               class="layout-row pa-0 my-1 mx-2"
               :class="{'elevation-5': compColor.isInDarkMode, 'grey lighten-3': !compColor.isInDarkMode}"
               style="border-radius:4px;"
               :style="{'border': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">

            <div class="layout-row layout-wrap flex-gt-sm-100 px-3">

              <!-- OP Code via Auto-Complete -->

              <div class="flex-gt-sm-25 flex-100 px-2 py-3">
                <validation-provider tag="div"
                                     :name="path + '.op_ops.' + index + '.code'"
                                     :ref="path + '.op_ops.' + index + '.code'"
                                     rules="required"
                                     v-slot="{ errors }">
                  <lx-autocomplete
                      :label="$t('FORMS_DIVERSE.BASE_OPERATION_OP_CODE') + ' *'"
                      v-model="item.code"
                      :error="errors[0]"
                      @search-method="getDataForAutocomplete"
                      @input="setOps"
                      :index="index"
                      :hide-details="!errors || errors.length <= 0"
                      :disabled="isDisabled"
                      :dense="denseFields"
                      :results="resultSearchModel">
                    <template v-slot="slotProps">
                      <div class="layout-row">
                        <div style="width: 100px;border-radius: 4px;" class="mr-2 px-2 white lighten-2 layout-column layout-align-center-center">
                          <h3>
                            <lx-text-highlight :text="slotProps.lineItem.code" :search-string="slotProps.searchText" :color="compColor.prime"></lx-text-highlight>
                          </h3>
                        </div>
                        <div class="flex">
                          <span class="body-2">
                            <lx-text-highlight :text="slotProps.lineItem.description" :search-string="slotProps.searchText" :color="compColor.prime"></lx-text-highlight>
                          </span>
                        </div>
                      </div>
                    </template>
                  </lx-autocomplete>
                </validation-provider>
              </div>

              <!-- ops-Version -->

              <div class="flex-gt-sm-25 flex-100 px-2 py-3">
                <validation-provider tag="div"
                                     :name="path + '.op_ops.' + index + '.version'"
                                     :ref="path + '.op_ops.' + index + '.version'"
                                     rules="required"
                                     v-slot="{ errors }">
                  <v-select
                    :color="compColor.primeColor"
                    :label="$t('FORMS_DIVERSE.BASE_OPERATION_OP_VERSION') + ' *'"
                    v-model="item.version"
                    :items="OP_OPS_VersionTypes"
                    :disabled="isDisabled"
                    outlined
                    class="mb-0"
                    @change="resetOpCode(index)"
                    :persistent-hint="true"
                    :hint="$t('FORMS_DIVERSE.CODE_VERSION_HINT')"
                    :dense="denseFields"
                    :error-messages="errors[0]"
                    :menu-props="{ bottom: true, offsetY: true }"
                    :dark="compColor.isInDarkMode">
                    <template v-slot:item="{ item, on, attrs }">
                      <v-list-item v-on="on"
                                   :class="attrs.inputValue ? [compColor.prime, 'lighten-4'] : compColor.isInDarkMode?['blue-grey darken-4'] : []">
                        <v-list-item-content class="py-0">
                          <label style="cursor:pointer;"
                                 :class="attrs.inputValue ? [compColor.primeClass] : compColor.isInDarkMode ? ['white--text'] : []"
                                 class="lighten-4 text--darken-2">{{ item.text }}</label>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-select>
                </validation-provider>
              </div>

              <!-- Beschreibungstext zur OP  -->

              <div class="flex-gt-sm-50 flex-100 px-2 py-3">
                <validation-provider tag="div"
                                     :name="path + '.op_ops.' + index + '.description'"
                                     :ref="path + '.op_ops.' + index + '.description'"
                                     rules=""
                                     v-slot="{ errors }">
                  <v-text-field
                      :label="$t('FORMS_DIVERSE.BASE_OPERATION_OP_DESCRIPTION')"
                      :color="compColor.primeColor"
                      :dark="compColor.isInDarkMode"
                      v-model="item.description"
                      disabled
                      :dense="denseFields"
                      outlined
                      :hide-details="!errors || errors.length <= 0"
                      :error-messages="errors[0]"
                      class="ma-0"
                  ></v-text-field>
                </validation-provider>
              </div>
            </div>

            <div class="layout-column layout-align-center-center px-3"
                 :class="{'grey lighten-2': !compColor.isInDarkMode}"
                 :style="{'border-left': compColor.isInDarkMode ? '1px solid #cccccc22 !important' : '1px solid #ccc !important;' }">
              <lx-btn @click="deleteItem(path + '.op_ops', index, path + '.op_ops.' + index + '.description')"
                      :color="compColor.primeColor"
                      :iconColor="'white'"
                      iconSize="21"
                      :width="35"
                      :height="35"
                      :disabled="isDisabled || (model.op_ops && model.op_ops.length === 1)"
                      icon="delete_forever"></lx-btn>
            </div>
          </div>
        </div>
      </fake-input-frame>
    </div>
  </div>
</template>

<script>
import metaData from '../shared/formsMetaData'
import utils from '../../../common/utils'
import LxAutocomplete from '../../lx_auto_complete/LxAutocomplete'

export default {
  name: "Grunddaten_Operation",
  mixins: [metaData, utils],
  components: {
    LxAutocomplete
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    path: {
      type: String
    },
    addOPSItem: {
      type: Function
    },
    deletOpsItem: {
      type: Function
    },
    isDisabled: {
      type: Boolean
    },
    validationRef: {
      type: Object,
      default: () => ({})
    },
    patientId: {
      type: String
    },
    formId: {
      type: String
    }
  },
  data () {
    return {
      model: {},
      OP_IntentionTypes: null,
      OP_OPS_VersionTypes: null,
      selectedIndex: null,
      resultSearchModel: {}
    }
  },
  created () {
    this.OP_IntentionTypes = this.formsMetaData.OP_IntentionTypes
    this.OP_OPS_VersionTypes = this.filterVersions('ops')
  },
  methods: {
    /**
     * Delete selected element of side effects and trigger validator for saving
     */
    deleteItem (path, index, field) {
      this.deletOpsItem(path, index)
      this.validationRef.fields[field].changed = true
    },

    /**
     * Delete ops-code and description after changing version
     *
     * @param index The index of morphology-element
     */
    resetOpCode (index) {
      this.$delete(this.model.op_ops[index], 'code')
      this.$delete(this.model.op_ops[index], 'description')
    },

    /**
     * Get list of ops codes via regex in auto-complete
     *
     * @param data The search string
     * @param index The index of selected item
     */
    getDataForAutocomplete (data, index) {
      if (data && this.model.op_ops[index].code && data !== this.model.op_ops[index].code) {
        this.$set(this.model.op_ops[index], 'description', undefined)
      }

      // clear string from special characters to provide errors
      data = this.clearStringForRegexSearch(data)

      let config = {
        opts: {
          limit: 40
        },
        query: {
          searchValue: data
        }
      }

      // if icd-10 version is set, send it to backend
      const foundVersion = this.OP_OPS_VersionTypes.find(elem => elem.value === this.model.op_ops[index].version)

      if (foundVersion?.value) {
        config.query.version = foundVersion.value
      }

      this.$http.get('/ops?config=' + JSON.stringify(config))
          .then(res => {
            this.resultSearchModel = {
              result: res.data.items,
              index: index,
              count: res.data.count || 0,
              version: res.data.version
            }
          })
          .catch(this.NotifyError)
    },

    /**
     * Set ops value via list form auto complete
     *
     * @param data The selected ops from list
     * @param index The index of element in ops-array
     */
    setOps (data, index) {
      if (data && data.code) {
        this.$set(this.model.op_ops[index], 'code', data.code)
        this.$set(this.model.op_ops[index], 'description', data.description)

        if (this.validationRef && this.validationRef.fields && this.validationRef.fields[this.path + '.op_ops.' + index + '.code']) {
          this.validationRef.fields[this.path + '.op_ops.' + index + '.code'].changed = true
        }

        if (!this.model.op_ops[index].version) {
          const version = this.resultSearchModel.version || this.formsMetaData.OPS_Version
          this.$set(this.model.op_ops[index], 'version', version)
        }
      } else {
        if (!this.model.op_ops[index].code || typeof data === 'string' || (this.validationRef.errors  && this.validationRef.errors[this.path + '.op_ops.' + index + '.code'] && this.validationRef.errors[this.path + '.op_ops.' + index + '.code'].length > 0)) {
          // nextTick is necessary to trigger watcher in autocomplete on the right way
          this.$nextTick(() => {
            // delete input of ops-code, if not selected from result list
            if (typeof data === 'string') this.$set(this.model.op_ops[index], 'code', null)
          })

          this.$set(this.model.op_ops[index], 'description', undefined)
        }
      }
    }
  },
  computed: {
    diagDate () {
      return this.patientId ? this.$store.getters.diagnosisBaseData(this.patientId).diagDate : undefined
    },
    deathDate () {
      return this.patientId ? this.$store.getters.diagnosisBaseData(this.patientId).deathDate : undefined
    }
  },
  watch: {
    model: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.$emit('input', newValue)
        }
      },
      immediate: true
    },
    value: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.model = newValue
        }
      },
      immediate: true
    }
    // 'model.op_ops': {
    //   deep: true,
    //   immediate: true,
    //   handler: function (newValue) {
    //     if (newValue && this.$refs[this.path + '.op_ops']) {
    //       this.$refs[this.path + '.op_ops'].validate()
    //     }
    //   }
    // }
  }
}
</script>

